import { createI18n } from "vue-i18n";

import _ from "lodash";

import { api } from "@/api/base";
import config from "@/config";
import { useMainStore } from "@/pinia/main";

export const i18n = createI18n({
  legacy: true,
  locale: "en",
  fallbackLocale: "en",
  messages: {},
  missingWarn: false,
  warnHtmlInMessage: "off",
  // we're getting these two below a lot when changing lang
  // so disabled the warnings for not flooding console log
  silentFallbackWarn: true,
  silentTranslationWarn: true,
});

const loadedLanguages: string[] = [];

function setI18nLanguage(lang: string) {
  i18n.global.locale = lang;
  useMainStore().setLanguage(lang);
  api.defaults.headers.common["Accept-Language"] = lang;
  document.querySelector("html")?.setAttribute("lang", lang);
  return lang;
}

export async function setLanguageAsync(lang: string, retryCount = 3): Promise<void> {
  if (!_.find(definedLanguages, { code: lang })) {
    throw new Error(`Unsupported language: ${lang}`);
  }

  if (loadedLanguages.includes(lang)) {
    setI18nLanguage(lang);
    return;
  }

  const proj = config.i18nProject || "default";
  const fallbackLang = "en";

  let attempt = 0;

  while (attempt <= retryCount) {
    try {
      const response = await import(`@/i18n/messages/${proj}/client-${lang}.json`);

      i18n.global.setLocaleMessage(lang, response.default);
      loadedLanguages.push(lang);
      setI18nLanguage(lang);
      return;
    } catch (error) {
      attempt++;
      console.error(`Attempt ${attempt} failed to load language file for ${lang}:`, error);

      // Wait briefly before retrying to avoid flooding requests
      await new Promise((res) => setTimeout(res, 500));

      // If all retry attempts fail, fall back to another language
      if (attempt > retryCount) {
        console.warn(`Failed to load ${lang} after ${retryCount} attempts.`);

        // Attempt fallback language if primary fails and isn't already the fallback
        if (lang !== fallbackLang) {
          console.warn(`Falling back to language: ${fallbackLang}`);
          setLanguageAsync(fallbackLang, retryCount);
          return;
        } else {
          throw new Error(
            `Failed to load both ${lang} and fallback language after ${retryCount} attempts`
          );
        }
      }
    }
  }
}

export async function loadLanguageAsync(lang: string, retryCount = 3): Promise<void> {
  if (!lang) throw new Error("Language code is required");
  if (!_.find(definedLanguages, { code: lang })) {
    throw new Error(`Unsupported language: ${lang}`);
  }
  if (loadedLanguages.includes(lang)) {
    return;
  }

  const proj = config.i18nProject || "default";
  let attempt = 0;

  while (attempt <= retryCount) {
    try {
      const response = await import(`@/i18n/messages/${proj}/client-${lang}.json`);

      i18n.global.setLocaleMessage(lang, response.default);
      loadedLanguages.push(lang);
      return;
    } catch (error) {
      attempt++;
      console.error(`Attempt ${attempt} failed to load language file for ${lang}:`, error);

      // Brief delay before retrying
      await new Promise((res) => setTimeout(res, 500));

      if (attempt > retryCount) {
        console.warn(`Failed to load ${lang} after ${retryCount} attempts.`);
        throw new Error(`Failed to load language: ${lang} after ${retryCount} attempts`);
      }
    }
  }
}

export function isLanguageLoaded(lang: string): boolean {
  return !!loadedLanguages?.includes?.(lang);
}

// Some convenience exports
export const definedLanguages = config?.i18nDefinedLanguages;
const definedCodes = definedLanguages.map((l) => l.code);

const editorCodes = _.difference(
  config?.i18nEditorLanguageIncludeCodes?.length ? config.i18nEditorLanguageIncludeCodes : ["en"],
  config.i18nEditorLanguageExcludeCodes || []
);
export const editorLanguages = _.filter(definedLanguages, (l) => _.includes(editorCodes, l.code));

const userProfileCodes = _.difference(
  config?.i18nUserProfileLanguageIncludeCodes?.length
    ? config.i18nUserProfileLanguageIncludeCodes
    : definedCodes,
  config.i18nUserProfileLanguageExcludeCodes || []
);
export const userProfileLanguages = _.filter(definedLanguages, (l) =>
  _.includes(userProfileCodes, l.code)
);

const contentCodes = _.difference(
  config?.i18nContentLanguageIncludeCodes?.length
    ? config.i18nContentLanguageIncludeCodes
    : definedCodes,
  config.i18nContentLanguageExcludeCodes || []
);
export const contentLanguages = _.filter(definedLanguages, (l) => _.includes(contentCodes, l.code));

const serviceCodes = _.difference(
  config?.i18nServiceLanguageIncludeCodes?.length
    ? config.i18nServiceLanguageIncludeCodes
    : definedCodes,
  config.i18nServiceLanguageExcludeCodes || []
);
export const serviceLanguages = _.filter(definedLanguages, (l) => _.includes(serviceCodes, l.code));

let intervalId: NodeJS.Timeout;
let isLoading = false;
let storeEditorLocale = "en";
let storeLocale = "en";
const browserLocale = navigator?.language?.slice?.(0, 2);
const defaultLocale = config.i18nDefaultLocale || "en";

// If we do not have any language loaded yet, we make an interval
// to periodically try and load the language
export function initLocaleFromStore() {
  const mainStore = useMainStore();
  storeLocale = mainStore.locale || storeLocale;
  storeEditorLocale = mainStore.editorLocale || storeEditorLocale;

  if (!loadedLanguages.length || !storeLocale) {
    let attempts = 0;
    intervalId = setInterval(() => {
      // get store locale again because it might be set in prev interval
      storeLocale = mainStore.locale || storeLocale;
      if (loadedLanguages.length && storeLocale) {
        initEditorLocale();
        clearInterval(intervalId);
        attempts = 0;
      }
      if (attempts > 10) {
        clearInterval(intervalId);
        console.log(`Could not load language after ${attempts} attempts.`);
      }
      if (isLoading) return;
      if (storeLocale && _.find(definedLanguages, { code: storeLocale })) {
        isLoading = true;
        setLanguageAsync(storeLocale)
          .catch((e) => {
            attempts++;
            if (attempts % 10 === 0) {
              console.log(
                `Could not load store language ${storeLocale} after ${attempts} attempts.`
              );
            }
          })
          .finally(() => {
            isLoading = false;
          });
      } else if (browserLocale && _.find(definedLanguages, { code: browserLocale })) {
        isLoading = true;
        setLanguageAsync(browserLocale)
          .catch((e) => {
            attempts++;
            if (attempts % 10 === 0) {
              console.log(
                `Could not load browser lang ${browserLocale} after ${attempts} attempts.`
              );
            }
          })
          .finally(() => {
            isLoading = false;
          });
      } else {
        isLoading = true;
        setLanguageAsync(defaultLocale)
          .catch((e) => {
            attempts++;
            if (attempts % 10 === 0) {
              console.log(
                `Could not load default lang ${defaultLocale} after ${attempts} attempts.`
              );
            }
          })
          .finally(() => {
            isLoading = false;
          });
      }
    }, 500);
  }
}

function initEditorLocale() {
  if (storeEditorLocale) return;
  const mainStore = useMainStore();
  if (_.find(editorLanguages, { code: storeLocale })) {
    mainStore.setEditorLanguage(storeLocale || "en");
  } else if (_.find(editorLanguages, { code: browserLocale })) {
    mainStore.setEditorLanguage(browserLocale);
  } else if (_.find(editorLanguages, { code: defaultLocale })) {
    mainStore.setEditorLanguage(defaultLocale);
  } else {
    mainStore.setEditorLanguage("en");
  }
}

export default i18n;
